import Vue from "vue";
import store from "@/store";
import Router, { NavigationGuard } from "vue-router";
import { isLoggedIn, logout } from "@/getinfo-api/auth";
import Home from "./views/Home.vue";
import Base from "./views/Base.vue";
import BaseAuth from "./views/BaseAuth.vue";
import {
  bookkeepingModule,
  integrationModule,
  learningControlModule,
  learningDocumentsModule,
  matomoModule,
  learningGroupModule,
  agentsModule
} from "@/plugins/modules-detect";
import { checkRoles, Roles } from "@/utils/check-access-to-route";
import CourseDocumentInfoCreate from "@/views/courses/CourseDocumentInfoCreate.vue";
import { i18n } from "@/locales/settings";
import { LocalesFields } from "@/locales/translateInterface";

// Lazy loading tasks views components.
const LoginView = () => import("@/views/auth/Login.vue");
const Registration = () => import("@/views/auth/Registration.vue");
const PasswordRecovery = () =>
  import("@/views/password-recovery/PasswordRecovery.vue");

const Agreement = () => import("@/views/Agreement.vue");

const CourseList = () => import("@/views/courses/CourseListView.vue");
const CourseView = () => import("@/views/courses/CourseView.vue");
const CourseNew = () => import("@/views/courses/CourseNew.vue");
const CourseEdit = () => import("@/views/courses/CourseEdit.vue");

const WebinarNew = () => import("@/views/webinars/WebinarNew.vue");
const WebinarsList = () => import("@/views/webinars/WebinarsList.vue");
const WebinarEdit = () => import("@/views/webinars/WebinarEdit.vue");

const MyProfileView = () => import("@/views/user/MyProfileView.vue");
const ProfileEdit = () => import("@/views/user/ProfileEdit.vue");

const UserProfileView = () => import("@/views/user/UserProfileView.vue");

const LectureView = () => import("@/views/lectures/LecturesView.vue");
const LectureNew = () => import("@/views/lectures/LectureNew.vue");
const LectureEdit = () => import("@/views/lectures/LectureEdit.vue");
const LectureList = () => import("@/views/lectures/LectureList.vue");
const Lecture = () => import("@/views/lectures/Lecture.vue");

const RecordsList = () => import("@/views/records/RecordsList.vue");
const RecordView = () => import("@/views/records/RecordView.vue");
const RecordNew = () => import("@/views/records/RecordNew.vue");
const RecordEdit = () => import("@/views/records/RecordEdit.vue");

const TestView = () => import("@/views/tests/TestView.vue");
const TestNew = () => import("@/views/tests/TestNew.vue");
const TestEdit = () => import("@/views/tests/TestEdit.vue");
const TestList = () => import("@/views/tests/TestList.vue");
const TestResult = () => import("@/views/tests/TestResults.vue");
const TestPassResultView = () => import("@/views/tests/TestPassResultView.vue");
const TestTraining = () =>
  import("@/views/tests/testTraining/TestTraining.vue");

const PracticeList = () => import("@/views/practice/PracticeList.vue");
const PracticeView = () => import("@/views/practice/PracticeView.vue");
const PracticeEdit = () => import("@/views/practice/PracticeEdit.vue");
const PracticeNew = () => import("@/views/practice/PracticeNew.vue");
const PracticePass = () => import("@/views/practice/PracticePass.vue");
const PracticePassForUserList = () =>
  import("@/views/practice/PracticePassForUserList.vue");

const UsersListView = () => import("@/views/admin/users/UsersListView.vue");
const CompaniesListView = () =>
  import("@/views/admin/companies/CompaniesListView.vue");

const EducationalOrganization = () =>
  import("@/views/companies/EducationalOrganization.vue");
const CreateCompany = () => import("@/views/companies/CreateCompanyView.vue");
const CompanyPage = () => import("@/views/companies/CompanyPageView.vue");
const CompanyEdit = () => import("@/views/companies/CompanyEditView.vue");

const OrdersList = () => import("@/views/orders/OrdersList.vue");
const CreateOrder = () => import("@/views/orders/OrderNew.vue");
const OrderItem = () => import("@/views/orders/OrderItem.vue");
const OrderEdit = () => import("@/views/orders/OrderEdit.vue");

const ContractorNew = () => import("@/views/contractors/ContractorNew.vue");
const ContractorList = () => import("@/views/contractors/ContractorList.vue");
const ContractorItemView = () =>
  import("@/views/contractors/ContractorItemView.vue");
const ContractorEdit = () => import("@/views/contractors/ContractorEdit.vue");

const CreateNewEmployee = () =>
  import("@/views/user/CreateNewEmployeeView.vue");

const Error = () => import("@/views/errors/Error.vue");

const CategoryNew = () => import("@/views/admin/category/CategoryNew.vue");
const CategoryList = () => import("@/views/admin/category/CategoryList.vue");

const ActView = () => import("@/views/act/ActView.vue");
const ActEdit = () => import("@/views/act/ActEdit.vue");
const ActList = () => import("@/views/act/ActList.vue");

const BillList = () => import("@/views/bill/BillList.vue");
const BillView = () => import("@/views/bill/BillView.vue");
const BillEdit = () => import("@/views/bill/BillEdit.vue");

const ContractNew = () => import("@/views/contract/ContractNew.vue");
const ContractView = () => import("@/views/contract/ContractView.vue");
const ContractEdit = () => import("@/views/contract/ContractEdit.vue");
const ContractList = () => import("@/views/contract/ContractList.vue");

const LearningProgressList = () =>
  import("@/views/learning-progress/LearningProgressList.vue");
const ListenerProgressView = () =>
  import("@/views/learning-progress/ListenerProgressView.vue");
const CourseRegistrationProgressView = () =>
  import("@/views/learning-progress/CourseRegistrationProgressView.vue");

const FinalDocumentNew = () =>
  import("@/views/final-document/FinalDocumentNew.vue");
const FinalDocument = () => import("@/views/final-document/FinalDocument.vue");
const FinalDocumentEdit = () =>
  import("@/views/final-document/FinalDocumentEdit.vue");
const FinalDocumentList = () =>
  import("@/views/final-document/FinalDocumentList.vue");

const EnrollmentOrderNew = () =>
  import("@/views/enrollment/EnrollmentOrderNew.vue");
const EnrollmentList = () =>
  import("@/views/enrollment/EnrollmentOrderList.vue");
const EnrollmentOrderView = () =>
  import("@/views/enrollment/EnrollmentOrderView.vue");
const EnrollmentOrderEdit = () =>
  import("@/views/enrollment/EnrollmentOrderEdit.vue");

const ExpulsionOrderList = () =>
  import("@/views/expulsion/ExpulsionOrderList.vue");
const ExpulsionOrderNew = () =>
  import("@/views/expulsion/ExpulsionOrderNew.vue");
const ExpulsionOrderView = () =>
  import("@/views/expulsion/ExpulsionOrderView.vue");
const ExpulsionOrderEdit = () =>
  import("@/views/expulsion/ExpulsionOrderEdit.vue");

const EducationProtocol = () =>
  import("@/views/education/EducationProtocol.vue");
const EducationProtocolList = () =>
  import("@/views/education/EducationProtocolList.vue");
const EducationProtocolNew = () =>
  import("@/views/education/EducationProtocolNew.vue");
const EducationProtocolEdit = () =>
  import("@/views/education/EducationProtocolEdit.vue");

const DocTemplates = () => import("@/views/document-template/DocTemplates.vue");
const DocTemplateNew = () =>
  import("@/views/document-template/DocTemplateNew.vue");
const DocTemplateEdit = () =>
  import("@/views/document-template/DocTemplateEdit.vue");

const InviteUsers = () => import("@/views/invite-users/InviteUsers.vue");
const ImportUsersFromCsv = () =>
  import("@/views/invite-users/ImportFromCsv.vue");

const ImportCourses = () =>
  import("@/views/import-course/ImportCourseView.vue");

const ImportContractors = () =>
  import("@/views/contractors/ImportContractors.vue");

const ProfessionalActivityTaxonomyList = () =>
  import("@/views/taxonomy/ProfessionalActivityTaxonomyList.vue");
const ProfessionalActivityTaxonomyNew = () =>
  import("@/views/taxonomy/ProfessionalActivityTaxonomyNew.vue");

const Notifications = () => import("@/views/user/UserNotificationList.vue");

const ApiKeysGeneratingForm = () =>
  import("@/views/integrations/ApiKeysGeneratingForm.vue");

const WidgetCreate = () => import("@/views/integrations/WidgetCreate.vue");
const WidgetEdit = () => import("@/views/integrations/WidgetEdit.vue");
const Widget = () => import("@/views/integrations/Widget.vue");
const AdminControl = () => import("@/views/admin/AdminControl.vue");

const Students = () => import("@/views/students/StudentsList.vue");
const StudentNew = () => import("@/views/students/StudentNew.vue");
const Student = () => import("@/views/students/Student.vue");
const StudentEdit = () => import("@/views/students/StudentEdit.vue");

const DocumentTypeTaxonomyCreate = () =>
  import("@/views/taxonomy/DocumentTypeTaxonomyNew.vue");
const DocumentTypeTaxonomyList = () =>
  import("@/views/taxonomy/DocumentTypeTaxonomyList.vue");

const DocumentNumberList = () =>
  import("@/views/document-number/DocumentNumberList.vue");

const Course = () => import("@/views/courses/CourseV2.vue");
const CourseCreateV2 = () => import("@/views/courses/CourseCreateV2.vue");
const CourseEditV2 = () => import("@/views/courses/CourseEditV2.vue");
const CourseScormCreate = () => import("@/views/courses/CourseScormCreate.vue");
const CourseInfoCreate = () => import("@/views/courses/CourseInfoCreate.vue");
const Lesson = () => import("@/views/courses/Lesson.vue");

const LearningRequestCreateV2 = () =>
  import("@/views/learning-request-v2/LearningRequestCreateV2.vue");
const LearningRequestEditV2 = () =>
  import("@/views/learning-request-v2/LearningRequestEditV2.vue");
const LearningRequest = () =>
  import("@/views/learning-request-v2/LearningRequest.vue");
const Profiles = () => import("@/views/learning-request-v2/Profiles.vue");
const Profile = () => import("@/views/learning-request-v2/Profile.vue");
const StatusProfileEdit = () =>
  import("@/views/learning-request-v2/ProfileEdit.vue");

const LearningGroup = () => import("@/views/learning-group/LearningGroup.vue");
const LearningGroupList = () =>
  import("@/views/learning-group/LearningGroupList.vue");
const LearningGroupNew = () =>
  import("@/views/learning-group/LearningGroupNew.vue");
const LearningGroupEdit = () =>
  import("@/views/learning-group/LearningGroupEdit.vue");

const ApiKeysMain = () => import("@/views/integrations/ApiKeysMain.vue");
const ImportedUsers = () => import("@/views/companies/ImportedUsers.vue");
const RequestHandler = () => import("@/views/orders/RequestHandling.vue");

const ImportView = () => import("@/views/import/ImportView.vue");
const ImportList = () => import("@/views/import/ImportList.vue");

const ExportList = () => import("@/views/export/ExportList.vue");
const ExportView = () => import("@/views/export/ExportView.vue");

const Instructions = () => import("@/views/Instructions/Instructions.vue");
const InstructionsView = () =>
  import("@/views/Instructions/InstructionsView.vue");

const AgentList = () => import("@/views/agent/AgentList.vue");
const AgentView = () => import("@/views/agent/AgentView.vue");
const AgentEdit = () => import("@/views/agent/AgentEdit.vue");
const AgentCreate = () => import("@/views/agent/AgentCreate.vue");

const ReportList = () => import("@/views/reports/ReportList.vue");

const ConditionList = () => import("@/views/conditions/ConditionList.vue");
const ConditionView = () => import("@/views/conditions/ConditionView.vue");
const ConditionEdit = () => import("@/views/conditions/ConditionEdit.vue");
const ConditionCreate = () => import("@/views/conditions/ConditionCreate.vue");

Vue.use(Router);

const middlewareLogged: NavigationGuard = (to, from, next) => {
  if (!isLoggedIn()) {
    document.title = to.meta?.title;
    next({
      name: "login"
    });
  } else {
    document.title = to.meta?.title;
    next();
  }
};

const docTemplateGuard: NavigationGuard = (to, from, next) => {
  if (!isLoggedIn()) {
    document.title = to.meta?.title;
    next({
      name: "login",
      query: {
        redirect: to.fullPath
      }
    });
  } else {
    if (learningDocumentsModule() || bookkeepingModule()) {
      document.title = to.meta?.title;
      next();
    } else {
      document.title = to.meta?.title;
      next({ name: "error" });
    }
  }
};

const agentsModuleGuard: NavigationGuard = (to, from, next) => {
  if (!isLoggedIn()) {
    document.title = to.meta?.title;
    next({
      name: "login",
      query: {
        redirect: to.fullPath
      }
    });
  } else {
    if (agentsModule()) {
      document.title = to.meta?.title;
      next();
    } else {
      document.title = to.meta?.title;
      next({ name: "error" });
    }
  }
};

const learningDocumentsModuleGuard: NavigationGuard = (to, from, next) => {
  if (!isLoggedIn()) {
    document.title = to.meta?.title;
    next({
      name: "login",
      query: {
        redirect: to.fullPath
      }
    });
  } else {
    if (learningDocumentsModule()) {
      document.title = to.meta?.title;
      next();
    } else {
      document.title = to.meta?.title;
      next({ name: "error" });
    }
  }
};

const bookkeepingModuleGuard: NavigationGuard = (to, from, next) => {
  if (!isLoggedIn()) {
    document.title = to.meta?.title;
    next({
      name: "login",
      query: {
        redirect: to.fullPath
      }
    });
  } else {
    if (bookkeepingModule()) {
      document.title = to.meta?.title;
      next();
    } else {
      document.title = to.meta?.title;
      next({ name: "error" });
    }
  }
};

const integrationGuard: NavigationGuard = (to, from, next) => {
  if (!isLoggedIn()) {
    document.title = to.meta?.title;
    next({
      name: "login",
      query: {
        redirect: to.fullPath
      }
    });
  } else {
    if (integrationModule()) {
      document.title = to.meta?.title;
      next();
    } else {
      document.title = to.meta?.title;
      next({ name: "error" });
    }
  }
};

const learningControlGuard: NavigationGuard = (to, from, next) => {
  if (!isLoggedIn()) {
    document.title = to.meta?.title;
    next({
      name: "login",
      query: {
        redirect: to.fullPath
      }
    });
  } else {
    if (learningControlModule()) {
      document.title = to.meta?.title;
      next();
    } else {
      document.title = to.meta?.title;
      next({ name: "error" });
    }
  }
};
const learningGroupGuard: NavigationGuard = (to, from, next) => {
  if (!isLoggedIn()) {
    document.title = to.meta?.title;
    next({
      name: "login",
      query: {
        redirect: to.fullPath
      }
    });
  } else {
    if (learningGroupModule()) {
      document.title = to.meta?.title;
      next();
    } else {
      document.title = to.meta?.title;
      next({ name: "error" });
    }
  }
};

const middlewareNotLogged: NavigationGuard = (to, from, next) => {
  if (isLoggedIn()) {
    document.title = to.meta?.title;
    next({ name: "home" });
  } else {
    document.title = to.meta?.title;
    next();
  }
};

const router = new Router({
  mode: "history",
  routes: [
    {
      path: "",
      component: Base,
      children: [
        {
          path: "/",
          name: "home",
          component: Home,
          meta: {
            title: "СДО"
          },
          beforeEnter: middlewareLogged
        },
        {
          path: "/agreement",
          name: "agreement",
          meta: {
            title: "Пользовательское соглашение"
          },
          component: Agreement,
          beforeEnter: middlewareLogged
        },
        {
          path: "/courses",
          name: "courses",
          meta: {
            title: i18n.tc(LocalesFields.Courses)
          },
          component: CourseList,
          beforeEnter: middlewareLogged
        },
        {
          path: "/course/:courseId",
          name: "courseView",
          meta: {
            title: i18n.tc(LocalesFields.Course)
          },
          component: CourseView,
          beforeEnter: middlewareLogged
        },
        {
          path: "/courseV2/:id",
          name: "course",
          meta: {
            title: i18n.tc(LocalesFields.Course)
          },
          component: Course,
          beforeEnter: middlewareLogged
        },
        {
          path: "/courses/new",
          name: "courseNew",
          meta: {
            title: i18n.tc(LocalesFields.NewCourse),
            onlyForEmployees: true,
            access: [Roles.ContentManager, Roles.CompanyOwner]
          },
          component: CourseNew,
          beforeEnter: middlewareLogged
        },
        {
          path: "/courses/scormAdd",
          name: "courseScormCreate",
          meta: {
            title: i18n.tc(LocalesFields.NewCourse),
            onlyForEmployees: true,
            access: [Roles.ContentManager, Roles.CompanyOwner]
          },
          component: CourseScormCreate,
          beforeEnter: middlewareLogged
        },
        {
          path: "/courses/create",
          name: "courseCreate",
          meta: {
            title: i18n.tc(LocalesFields.NewCourse),
            onlyForEmployees: true,
            access: [Roles.ContentManager, Roles.CompanyOwner]
          },
          component: CourseCreateV2,
          beforeEnter: middlewareLogged
        },
        {
          path: "/course/:courseId/edit",
          name: "courseEdit",
          meta: {
            title: i18n.tc(LocalesFields.Edit),
            onlyForEmployees: true
          },
          component: CourseEdit,
          beforeEnter: middlewareLogged
        },
        {
          path: "/courseV2/:id/edit",
          name: "courseEditV2",
          meta: {
            title: i18n.tc(LocalesFields.Edit),
            onlyForEmployees: true
          },
          component: CourseEditV2,
          beforeEnter: middlewareLogged
        },
        {
          path: "/courseV2/:id/course-info-create",
          name: "courseInfoCreate",
          meta: {
            title: i18n.tc(LocalesFields.MainInformation),
            onlyForEmployees: true
          },
          component: CourseInfoCreate,
          beforeEnter: middlewareLogged
        },
        {
          path: "/courseV2/:id/course-document-info-create",
          name: "courseDocumentInfoCreate",
          meta: {
            title: i18n.tc(LocalesFields.MainInformation),
            onlyForEmployees: true
          },
          component: CourseDocumentInfoCreate,
          beforeEnter: middlewareLogged
        },
        {
          path: "/webinars",
          name: "webinars",
          meta: {
            title: i18n.tc(LocalesFields.Webinars)
          },
          component: WebinarsList,
          beforeEnter: middlewareLogged
        },
        {
          path: "/webinars/new",
          name: "webinarNew",
          meta: {
            title: i18n.tc(LocalesFields.NewWebinar),
            onlyForEmployees: true
          },
          component: WebinarNew,
          beforeEnter: middlewareLogged
        },
        {
          path: "/webinar/:webinarId/edit",
          name: "webinarEdit",
          meta: {
            title: i18n.tc(LocalesFields.EditWebinar),
            onlyForEmployees: true
          },
          component: WebinarEdit,
          beforeEnter: middlewareLogged
        },
        {
          path: "/course/:courseId/lecture/:courseLectureId",
          name: "lectureView",
          meta: {
            title: "Лекция"
          },
          component: LectureView,
          beforeEnter: middlewareLogged
        },
        {
          path: "/lectures/new",
          name: "lectureNew",
          meta: {
            title: i18n.tc(LocalesFields.NewLecture),
            onlyForEmployees: true
          },
          component: LectureNew,
          beforeEnter: middlewareLogged
        },
        {
          path: "/lecture/:id/edit",
          name: "lectureEdit",
          meta: {
            title: i18n.tc(LocalesFields.Edit),
            onlyForEmployees: true
          },
          component: LectureEdit,
          beforeEnter: middlewareLogged
        },
        {
          path: "/lecture/:id",
          name: "lecture",
          meta: {
            title: i18n.tc(LocalesFields.Lecture),
            onlyForEmployees: true
          },
          component: Lecture,
          beforeEnter: middlewareLogged
        },
        {
          path: "/lecture-list",
          name: "lectureList",
          meta: {
            title: i18n.tc(LocalesFields.Lectures),
            onlyForEmployees: true
          },
          component: LectureList,
          beforeEnter: middlewareLogged
        },
        {
          path: "/records",
          name: "records",
          meta: {
            title: i18n.tc(LocalesFields.Records)
          },
          component: RecordsList,
          beforeEnter: middlewareLogged
        },
        {
          path: "/records/:id",
          name: "recordView",
          meta: {
            title: i18n.tc(LocalesFields.Record)
          },
          component: RecordView,
          beforeEnter: middlewareLogged
        },
        {
          path: "/record/new",
          name: "recordNew",
          meta: {
            title: i18n.tc(LocalesFields.CreateRecord),
            onlyForEmployees: true
          },
          component: RecordNew,
          beforeEnter: middlewareLogged
        },
        {
          path: "/records/:id/edit",
          name: "recordEdit",
          meta: {
            title: i18n.tc(LocalesFields.EditRecordedWebinar),
            onlyForEmployees: true
          },
          component: RecordEdit,
          beforeEnter: middlewareLogged
        },
        {
          path: "/course/:courseId/test",
          name: "courseTest",
          meta: {
            title: i18n.tc(LocalesFields.Test)
          },
          component: TestView,
          beforeEnter: middlewareLogged
        },
        {
          path: "/test-result",
          name: "testResult",
          meta: {
            title: i18n.tc(LocalesFields.ResultsOfTests)
          },
          component: TestResult,
          beforeEnter: middlewareLogged
        },
        {
          path: "/test-result-company",
          name: "testResultCompany",
          meta: {
            title: i18n.tc(LocalesFields.ResultsOfTests),
            onlyForEmployees: true
          },
          component: TestResult,
          beforeEnter: middlewareLogged
        },
        {
          path: "/test-result/:testPassId",
          name: "TestPassResultView",
          meta: {
            title: i18n.tc(LocalesFields.TestResult)
          },
          component: TestPassResultView,
          beforeEnter: middlewareLogged
        },
        {
          path: "/course/:courseId/lecture/:courseLectureId/test",
          name: "courseLectureTest",
          meta: {
            title: i18n.tc(LocalesFields.Test)
          },
          component: TestView,
          beforeEnter: middlewareLogged
        },
        {
          path: "/test/:testId/edit",
          name: "testEdit",
          meta: {
            title: i18n.tc(LocalesFields.Edit),
            onlyForEmployees: true
          },
          component: TestEdit,
          beforeEnter: middlewareLogged
        },
        {
          path: "/test/new",
          name: "testNew",
          meta: {
            title: i18n.tc(LocalesFields.NewTest),
            onlyForEmployees: true
          },
          component: TestNew,
          beforeEnter: middlewareLogged
        },
        {
          path: "/tests-list",
          name: "testList",
          meta: {
            title: i18n.tc(LocalesFields.Tests)
          },
          component: TestList,
          beforeEnter: middlewareLogged
        },
        {
          path: "/test/:id/training",
          name: "testTraining",
          meta: {
            title: "Тренировочный тест"
          },
          component: TestTraining,
          beforeEnter: middlewareLogged
        },
        {
          path: "/:courseId/test/:id/training",
          name: "testTrainingCourse",
          meta: {
            title: "Тренировочный тест"
          },
          component: TestTraining,
          beforeEnter: middlewareLogged
        },
        {
          path: "/:courseId/:lectureId/test/:id/training",
          name: "testTrainingLecture",
          meta: {
            title: "Тренировочный тест"
          },
          component: TestTraining,
          beforeEnter: middlewareLogged
        },
        {
          path: "/profile/:id",
          name: "userProfileView",
          meta: {
            title: "Профиль"
          },
          component: UserProfileView,
          beforeEnter: middlewareLogged
        },
        {
          path: "/my-profile",
          name: "myProfile",
          meta: {
            title: "Мой профиль"
          },
          component: MyProfileView,
          beforeEnter: middlewareLogged
        },
        {
          path: "/profile/edit/:id",
          name: "profileEdit",
          meta: {
            title: i18n.tc(LocalesFields.Edit)
          },
          component: ProfileEdit,
          beforeEnter: middlewareLogged
        },
        {
          path: "/practices",
          name: "practiceList",
          meta: {
            title: "Практические задания",
            onlyForEmployees: true
          },
          component: PracticeList,
          beforeEnter: middlewareLogged
        },
        {
          path: "/my-practices",
          name: "myPractices",
          meta: {
            title: "Мои практические задания"
          },
          component: PracticePassForUserList,
          beforeEnter: middlewareLogged
        },
        {
          path: "/course/:courseId/practice",
          name: "practiceView",
          meta: {
            title: i18n.tc(LocalesFields.PracticeAssignment)
          },
          component: PracticeView,
          beforeEnter: middlewareLogged
        },
        {
          path: "/practice/new",
          name: "practiceNew",
          meta: {
            title: i18n.tc(LocalesFields.NewPracticeAssignment),
            onlyForEmployees: true
          },
          component: PracticeNew,
          beforeEnter: middlewareLogged
        },
        {
          path: "/course/:courseId/practice/:practiceId/edit",
          name: "practiceEdit",
          meta: {
            title: i18n.tc(LocalesFields.Edit),
            onlyForEmployees: true
          },
          component: PracticeEdit,
          beforeEnter: middlewareLogged
        },
        {
          path: "/course/:courseId/practice/:listenerId",
          name: "practicePass",
          meta: {
            title: i18n.tc(LocalesFields.PracticeAssignment)
          },
          component: PracticePass,
          beforeEnter: middlewareLogged
        },
        {
          path: "/final-document/:id",
          name: "finalDocument",
          meta: {
            title: i18n.tc(LocalesFields.FinalDocument),
            onlyForEmployees: true
          },
          component: FinalDocument,
          beforeEnter: learningDocumentsModuleGuard
        },
        {
          path: "/final-document-new",
          name: "finalDocumentNew",
          meta: {
            title: i18n.tc(LocalesFields.NewFinalDocument),
            onlyForEmployees: true
          },
          component: FinalDocumentNew,
          beforeEnter: learningDocumentsModuleGuard
        },
        {
          path: "/final-document/:id/edit",
          name: "finalDocumentEdit",
          meta: {
            title: i18n.tc(LocalesFields.Edit),
            onlyForEmployees: true
          },
          component: FinalDocumentEdit,
          beforeEnter: learningDocumentsModuleGuard
        },
        {
          path: "/final-document-list",
          name: "finalDocumentList",
          meta: {
            title: i18n.tc(LocalesFields.FinalDocuments),
            onlyForEmployees: true
          },
          component: FinalDocumentList,
          beforeEnter: learningDocumentsModuleGuard
        },
        {
          path: "/users",
          name: "users",
          meta: {
            title: i18n.tc(LocalesFields.Users)
          },
          component: UsersListView,
          beforeEnter: middlewareLogged
        },
        {
          path: "/notifications",
          name: "notifications",
          meta: {
            title: i18n.tc(LocalesFields.Notifications)
          },
          component: Notifications,
          beforeEnter: middlewareLogged
        },
        {
          path: "/companies",
          name: "companies",
          meta: {
            title: i18n.tc(LocalesFields.Companies)
          },
          component: CompaniesListView,
          beforeEnter: middlewareLogged
        },
        {
          path: "/company/create-company",
          name: "createCompany",
          meta: {
            title: i18n.tc(LocalesFields.CreateCompany)
          },
          component: CreateCompany,
          beforeEnter: middlewareLogged
        },
        {
          path: "/company/:id/edit",
          name: "companyEdit",
          meta: {
            title: i18n.tc(LocalesFields.Edit),
            onlyForEmployees: true
          },
          component: CompanyEdit,
          beforeEnter: middlewareLogged
        },
        {
          path: "/company/:id",
          name: "companyPage",
          meta: {
            title: i18n.tc(LocalesFields.Company)
          },
          component: CompanyPage,
          beforeEnter: middlewareLogged
        },
        {
          path: "/educational",
          name: "EducationalOrganization",
          meta: {
            title: i18n.tc(
              LocalesFields.InformationAboutEducationalOrganization
            )
          },
          component: EducationalOrganization,
          beforeEnter: middlewareLogged
        },
        {
          path: "/import-courses",
          name: "importCourses",
          meta: {
            title: i18n.tc(LocalesFields.ImportCourses)
          },
          component: ImportCourses,
          beforeEnter: middlewareLogged
        },
        {
          path: "/import-contractors",
          name: "importContractors",
          meta: {
            title: i18n.tc(LocalesFields.ImportContractors)
          },
          component: ImportContractors,
          beforeEnter: middlewareLogged
        },
        {
          path: "/registration",
          name: "registration",
          meta: {
            title: i18n.tc(LocalesFields.RegisterUser)
          },
          component: Registration,
          beforeEnter: middlewareLogged
        },
        {
          path: "/create-employee",
          name: "createEmployee",
          meta: {
            title: i18n.tc(LocalesFields.AddEmployee),
            onlyForEmployees: true
          },
          component: CreateNewEmployee,
          beforeEnter: middlewareLogged
        },
        {
          path: "/category",
          name: "categoryList",
          meta: {
            title: i18n.tc(LocalesFields.LearningDirection),
            onlyForEmployees: true
          },
          component: CategoryList,
          beforeEnter: middlewareLogged
        },
        {
          path: "/category/new",
          name: "categoryNew",
          meta: {
            title: i18n.tc(LocalesFields.CreateDirectionOfLearning),
            onlyForEmployees: true,
            allowedRoles: []
          },
          component: CategoryNew,
          beforeEnter: middlewareLogged
        },
        {
          path: "/orders",
          name: "orders",
          meta: {
            title: i18n.tc(LocalesFields.LearningRequests)
            // onlyForEmployees: true
          },
          component: OrdersList,
          beforeEnter: middlewareLogged
        },
        {
          path: "/create-order",
          name: "createOrder",
          meta: {
            title: i18n.tc(LocalesFields.NewLearningRequest)
            // onlyForEmployees: true
          },
          component: CreateOrder,
          beforeEnter: middlewareLogged
        },
        {
          path: "/order/:orderId",
          name: "orderItem",
          meta: {
            title: i18n.tc(LocalesFields.LearningRequest)
            // onlyForEmployees: true
          },
          component: OrderItem,
          beforeEnter: middlewareLogged
        },
        {
          path: "/request-handler",
          name: "requestHandler",
          meta: {
            title: i18n.tc(LocalesFields.LearningRequest),
            onlyForEmployees: true
          },
          component: RequestHandler,
          beforeEnter: middlewareLogged
        },
        {
          path: "/order/:orderId/edit",
          name: "orderEdit",
          meta: {
            title: i18n.tc(LocalesFields.Edit),
            onlyForEmployees: true
          },
          component: OrderEdit,
          beforeEnter: middlewareLogged
        },
        {
          path: "/learning-request/create",
          name: "learningRequestCreate",
          meta: {
            title: i18n.tc(LocalesFields.Edit),
            onlyForEmployees: true
          },
          component: LearningRequestCreateV2,
          beforeEnter: middlewareLogged
        },
        {
          path: "/learning-request/:id/edit",
          name: "learningRequestEdit",
          meta: {
            title: i18n.tc(LocalesFields.Edit),
            onlyForEmployees: true
          },
          component: LearningRequestEditV2,
          beforeEnter: middlewareLogged
        },
        {
          path: "/learning-request/:id",
          name: "learningRequest",
          meta: {
            title: i18n.tc(LocalesFields.LearningRequests),
            onlyForEmployees: true
          },
          component: LearningRequest,
          beforeEnter: middlewareLogged
        },
        {
          path: "/profiles/:id",
          name: "profile",
          meta: {
            title: "Профиль",
            onlyForEmployees: true
          },
          component: Profile,
          beforeEnter: middlewareLogged
        },
        {
          path: "/profiles/:id/edit",
          name: "statusProfileEdit",
          meta: {
            title: "Профиль",
            onlyForEmployees: true
          },
          component: StatusProfileEdit,
          beforeEnter: middlewareLogged
        },
        {
          path: "/profiles",
          name: "profiles",
          meta: {
            title: "Профили заявок",
            onlyForEmployees: true
          },
          component: Profiles,
          beforeEnter: middlewareLogged
        },
        {
          path: "/create-contractor",
          name: "createContractor",
          meta: {
            title: i18n.tc(LocalesFields.NewContractor),
            onlyForEmployees: true
          },
          component: ContractorNew,
          beforeEnter: middlewareLogged
        },
        {
          path: "/contractor-list",
          name: "contractorList",
          meta: {
            title: i18n.tc(LocalesFields.Contractors)
            // onlyForEmployees: true
          },
          component: ContractorList,
          beforeEnter: middlewareLogged
        },
        {
          path: "/contractor/:contractorId",
          name: "contractorItem",
          meta: {
            title: i18n.tc(LocalesFields.Contractor)
          },
          component: ContractorItemView,
          beforeEnter: middlewareLogged
        },
        {
          path: "/invite-users",
          name: "inviteUsers",
          meta: {
            title: "Приглашение пользователей",
            onlyForEmployees: true
          },
          component: InviteUsers,
          beforeEnter: middlewareLogged
        },
        {
          path: "/import-users",
          name: "importUsers",
          meta: {
            title: i18n.tc(LocalesFields.ImportOfUsers),
            onlyForEmployees: true
          },
          component: ImportUsersFromCsv,
          beforeEnter: middlewareLogged
        },
        {
          path: "/create-contract",
          name: "createContract",
          meta: {
            title: i18n.tc(LocalesFields.NewContract),
            onlyForEmployees: true
          },
          component: ContractNew,
          beforeEnter: bookkeepingModuleGuard
        },
        {
          path: "/contract/:contractId",
          name: "contractItem",
          meta: {
            title: i18n.tc(LocalesFields.Contract),
            onlyForEmployees: true
          },
          component: ContractView,
          beforeEnter: bookkeepingModuleGuard
        },
        {
          path: "/contract/:contractId/edit",
          name: "contractEdit",
          meta: {
            title: i18n.tc(LocalesFields.Edit),
            onlyForEmployees: true
          },
          component: ContractEdit,
          beforeEnter: bookkeepingModuleGuard
        },
        {
          path: "/taxonomy-activity",
          name: "ProfessionalActivityTaxonomyList",
          meta: {
            title: i18n.tc(
              LocalesFields.FISFRDOFederalInformationSystemTheFederalRegisterOfDataOnEducationDocumentsAndorAboutQualificationDocumentsOnTraining
            ),
            onlyForEmployees: true
          },
          component: ProfessionalActivityTaxonomyList,
          beforeEnter: learningDocumentsModuleGuard
        },
        {
          path: "/taxonomy-activity/new",
          name: "taxonomyProfessionalActivityNew",
          meta: {
            title: "Новое наименование области профессиональной деятельности",
            onlyForEmployees: true
          },
          component: ProfessionalActivityTaxonomyNew,
          beforeEnter: learningDocumentsModuleGuard
        },
        {
          path: "/contracts",
          name: "contracts",
          meta: {
            title: i18n.tc(LocalesFields.Contracts),
            onlyForEmployees: true
          },
          component: ContractList,
          beforeEnter: bookkeepingModuleGuard
        },
        {
          path: "/education-protocol/:id",
          name: "educationProtocol",
          meta: {
            title: i18n.tc(LocalesFields.TrainingProtocol),
            onlyForEmployees: true
          },
          component: EducationProtocol,
          beforeEnter: learningDocumentsModuleGuard
        },
        {
          path: "/conditions",
          name: "conditionList",
          meta: {
            title: i18n.tc(LocalesFields.PaymentTerms),
            onlyForEmployees: true
          },
          component: ConditionList,
          beforeEnter: agentsModuleGuard
        },
        {
          path: "/condition/:id",
          name: "conditionView",
          meta: {
            title: i18n.tc(LocalesFields.PaymentTerms),
            onlyForEmployees: true
          },
          component: ConditionView,
          beforeEnter: agentsModuleGuard
        },
        {
          path: "/condition/:id/edit",
          name: "conditionEdit",
          meta: {
            title: i18n.tc(LocalesFields.Edit),
            onlyForEmployees: true
          },
          component: ConditionEdit,
          beforeEnter: agentsModuleGuard
        },
        {
          path: "/condition-create",
          name: "conditionCreate",
          meta: {
            title: i18n.tc(LocalesFields.CreatePaymentTerms),
            onlyForEmployees: true
          },
          component: ConditionCreate,
          beforeEnter: agentsModuleGuard
        },
        {
          path: "/agents",
          name: "agents",
          meta: {
            title: i18n.tc(LocalesFields.Agents),
            onlyForEmployees: true
          },
          component: AgentList,
          beforeEnter: agentsModuleGuard
        },
        {
          path: "/agent/:id",
          name: "agentView",
          meta: {
            title: i18n.tc(LocalesFields.Agent),
            onlyForEmployees: true
          },
          component: AgentView,
          beforeEnter: agentsModuleGuard
        },
        {
          path: "/agent-create",
          name: "agentCreate",
          meta: {
            title: i18n.tc(LocalesFields.NewAgent),
            onlyForEmployees: true
          },
          component: AgentCreate,
          beforeEnter: agentsModuleGuard
        },
        {
          path: "/agent/:id/edit",
          name: "agentEdit",
          meta: {
            title: i18n.tc(LocalesFields.Edit),
            onlyForEmployees: true
          },
          component: AgentEdit,
          beforeEnter: agentsModuleGuard
        },
        {
          path: "/reports",
          name: "reports",
          meta: {
            title: i18n.tc(LocalesFields.Reports),
            onlyForEmployees: true
          },
          component: ReportList,
          beforeEnter: agentsModuleGuard
        },
        {
          path: "/education-protocol-list",
          name: "educationProtocolList",
          meta: {
            title: "Протоколы обучения",
            onlyForEmployees: true
          },
          component: EducationProtocolList,
          beforeEnter: learningDocumentsModuleGuard
        },
        {
          path: "/education-protocol-new",
          name: "educationProtocolNew",
          meta: {
            title: i18n.tc(LocalesFields.NewTrainingProtocol),
            onlyForEmployees: true
          },
          component: EducationProtocolNew,
          beforeEnter: learningDocumentsModuleGuard
        },
        {
          path: "/education-protocol/:id/edit",
          name: "educationProtocolEdit",
          meta: {
            title: i18n.tc(LocalesFields.Edit),
            onlyForEmployees: true
          },
          component: EducationProtocolEdit,
          beforeEnter: learningDocumentsModuleGuard
        },
        {
          path: "/learning-progress",
          name: "learningProgress",
          meta: {
            title: i18n.tc(LocalesFields.LearnerProgress),
            onlyForEmployees: true
          },
          component: LearningProgressList,
          beforeEnter: learningControlGuard
        },
        {
          path: "/learning-progress/:courseId/:listenerId",
          name: "listenerProgress",
          meta: {
            title: i18n.tc(LocalesFields.LearnerProgress),
            onlyForEmployees: true
          },
          component: ListenerProgressView,
          beforeEnter: learningControlGuard
        },
        {
          path: "/course-progress/:id",
          name: "courseRegistrationProgress",
          meta: {
            title: i18n.tc(LocalesFields.LearnerProgress),
            onlyForEmployees: true
          },
          component: CourseRegistrationProgressView,
          beforeEnter: learningControlGuard
        },
        {
          path: "/integrations",
          name: "apiKeysMain",
          meta: {
            title: i18n.tc(LocalesFields.Integration),
            onlyForEmployees: true
          },
          component: ApiKeysMain,
          beforeEnter: integrationGuard
        },
        {
          path: "/api-keys-generating",
          name: "apiKeysGenerating",
          meta: {
            title: i18n.tc(LocalesFields.APIKeyGenerating),
            onlyForEmployees: true
          },
          component: ApiKeysGeneratingForm,
          beforeEnter: integrationGuard
        },
        {
          path: "/widget/:id",
          name: "widget",
          meta: {
            title: i18n.tc(LocalesFields.CreateAWidget),
            onlyForEmployees: true
          },
          component: Widget,
          beforeEnter: integrationGuard
        },
        {
          path: "/widget-create",
          name: "widgetCreate",
          meta: {
            title: i18n.tc(LocalesFields.CreateAWidget),
            onlyForEmployees: true
          },
          component: WidgetCreate,
          beforeEnter: integrationGuard
        },
        {
          path: "/widget/:id/edit",
          name: "widgetEdit",
          meta: {
            title: i18n.tc(LocalesFields.Edit),
            onlyForEmployees: true
          },
          component: WidgetEdit,
          beforeEnter: integrationGuard
        },
        {
          path: "/bills",
          name: "bills",
          meta: {
            title: i18n.tc(LocalesFields.Bills),
            onlyForEmployees: true
          },
          component: BillList,
          beforeEnter: bookkeepingModuleGuard
        },
        {
          path: "/bill/:billId",
          name: "bill",
          meta: {
            title: i18n.tc(LocalesFields.Bill),
            onlyForEmployees: true
          },
          component: BillView,
          beforeEnter: bookkeepingModuleGuard
        },
        {
          path: "/bill/:billId/edit",
          name: "billEdit",
          component: BillEdit,
          meta: {
            title: i18n.tc(LocalesFields.Edit),
            onlyForEmployees: true
          },
          beforeEnter: bookkeepingModuleGuard
        },
        {
          path: "/contractor/:contractorId/edit",
          name: "contractorItemEdit",
          component: ContractorEdit,
          meta: {
            title: i18n.tc(LocalesFields.Edit),
            onlyForEmployees: true
          },
          beforeEnter: middlewareLogged
        },
        {
          path: "/acts",
          name: "acts",
          component: ActList,
          meta: {
            title: i18n.tc(LocalesFields.CertificatesOfCompletedWorks),
            onlyForEmployees: true
          },
          beforeEnter: bookkeepingModuleGuard
        },
        {
          path: "/act/:actId",
          name: "act",
          meta: {
            title: "Акт",
            onlyForEmployees: true
          },
          component: ActView,
          beforeEnter: bookkeepingModuleGuard
        },
        {
          path: "/act/:actId/edit",
          name: "actEdit",
          meta: {
            title: i18n.tc(LocalesFields.Edit),
            onlyForEmployees: true
          },
          component: ActEdit,
          beforeEnter: bookkeepingModuleGuard
        },
        {
          path: "/create-enrollment",
          name: "enrollmentCreate",
          meta: {
            title: i18n.tc(LocalesFields.NewOrderOfEnrollment),
            onlyForEmployees: true
          },
          component: EnrollmentOrderNew,
          beforeEnter: learningDocumentsModuleGuard
        },
        {
          path: "/edit-enrollment/:enrollmentId",
          name: "enrollmentEdit",
          meta: {
            title: i18n.tc(LocalesFields.Edit),
            onlyForEmployees: true
          },
          component: EnrollmentOrderEdit,
          beforeEnter: learningDocumentsModuleGuard
        },
        {
          path: "/enrollment-list",
          name: "enrollmentList",
          meta: {
            title: i18n.tc(LocalesFields.OrdersOfEnrollment),
            onlyForEmployees: true
          },
          component: EnrollmentList,
          beforeEnter: learningDocumentsModuleGuard
        },
        {
          path: "/enrollment/:enrollmentId",
          name: "enrollmentOrder",
          meta: {
            title: i18n.tc(LocalesFields.OrderOfEnrollment),
            onlyForEmployees: true
          },
          component: EnrollmentOrderView,
          beforeEnter: learningDocumentsModuleGuard
        },
        {
          path: "/expulsion-list",
          name: "expulsionList",
          meta: {
            title: i18n.tc(LocalesFields.OrderOfCompletion),
            onlyForEmployees: true
          },
          component: ExpulsionOrderList,
          beforeEnter: learningDocumentsModuleGuard
        },
        {
          path: "/create-expulsion",
          name: "ExpulsionOrderNew",
          meta: {
            title: i18n.tc(LocalesFields.NewOrderOfCompletion),
            onlyForEmployees: true
          },
          component: ExpulsionOrderNew,
          beforeEnter: learningDocumentsModuleGuard
        },
        {
          path: "/expulsion/:expulsionId",
          name: "expulsionOrder",
          meta: {
            title: i18n.tc(LocalesFields.OrderOfCompletion),
            onlyForEmployees: true
          },
          component: ExpulsionOrderView,
          beforeEnter: learningDocumentsModuleGuard
        },
        {
          path: "/edit-expulsion/:expulsionId",
          name: "expulsionEdit",
          meta: {
            title: i18n.tc(LocalesFields.Edit),
            onlyForEmployees: true
          },
          component: ExpulsionOrderEdit,
          beforeEnter: learningDocumentsModuleGuard
        },
        {
          path: "/document-templates",
          name: "docTemplates",
          meta: {
            title: i18n.tc(LocalesFields.Template),
            onlyForEmployees: true
          },
          component: DocTemplates,
          beforeEnter: docTemplateGuard
        },
        {
          path: "/document-templates/create",
          name: "docTemplatesCreate",
          meta: {
            title: i18n.tc(LocalesFields.NewDocumentTemplate),
            onlyForEmployees: true
          },
          component: DocTemplateNew,
          beforeEnter: docTemplateGuard
        },
        {
          path: "document-type/create",
          name: "documentTypeTaxonomyCreate",
          meta: {
            title: i18n.tc(LocalesFields.NewDocumentType),
            onlyForEmployees: true
          },
          component: DocumentTypeTaxonomyCreate,
          beforeEnter: learningDocumentsModuleGuard
        },
        {
          path: "document-type",
          name: "documentTypeTaxonomyList",
          meta: {
            title: "Типы документов",
            onlyForEmployees: true
          },
          component: DocumentTypeTaxonomyList,
          beforeEnter: learningDocumentsModuleGuard
        },
        {
          path: "/document-templates/:id/edit",
          name: "docTemplateEdit",
          meta: {
            title: i18n.tc(LocalesFields.Edit),
            onlyForEmployees: true
          },
          component: DocTemplateEdit,
          beforeEnter: docTemplateGuard
        },
        {
          path: "/document-numbers",
          name: "documentNumbers",
          meta: {
            title: "Нумерации документов",
            onlyForEmployees: true
          },
          component: DocumentNumberList,
          beforeEnter: middlewareLogged
        },
        {
          path: "/AdminControl",
          name: "adminControl",
          meta: {
            title: "Админ панель",
            onlyForEmployees: true,
            access: [Roles.Root]
          },
          component: AdminControl,
          beforeEnter: middlewareLogged
        },
        {
          path: "/students",
          name: "students",
          meta: {
            title: i18n.tc(LocalesFields.Students),
            onlyForEmployees: true
          },
          component: Students,
          beforeEnter: middlewareLogged
        },
        {
          path: "/students/create",
          name: "studentsCreate",
          meta: {
            title: i18n.tc(LocalesFields.NewStudent),
            onlyForEmployees: true
          },
          component: StudentNew,
          beforeEnter: middlewareLogged
        },
        {
          path: "/students/:id",
          name: "student",
          props: true,
          meta: {
            title: i18n.tc(LocalesFields.Student),
            onlyForEmployees: true
          },
          component: Student,
          beforeEnter: middlewareLogged
        },
        {
          path: "/students/:id/edit",
          name: "studentEdit",
          props: true,
          meta: {
            title: i18n.tc(LocalesFields.Student),
            onlyForEmployees: true
          },
          component: StudentEdit,
          beforeEnter: middlewareLogged
        },
        {
          path: "/lesson/:courseId/:id",
          name: "lesson",
          props: true,
          meta: {
            title: "Урок",
            onlyForEmployees: false
          },
          component: Lesson,
          beforeEnter: middlewareLogged
        },
        {
          path: "/imported-users",
          name: "importedUsers",
          props: true,
          meta: {
            title: "Импортированные пользователи",
            onlyForEmployees: false
          },
          component: ImportedUsers,
          beforeEnter: middlewareLogged
        },
        {
          path: "/learning-group-new",
          name: "learningGroupNew",
          props: true,
          meta: {
            title: "Создать группу обучения",
            onlyForEmployees: true
          },
          component: LearningGroupNew,
          beforeEnter: learningGroupGuard
        },
        {
          path: "/learning-group-list",
          name: "learningGroupList",
          props: true,
          meta: {
            title: i18n.tc(LocalesFields.TrainingGroups),
            onlyForEmployees: true
          },
          component: LearningGroupList,
          beforeEnter: learningGroupGuard
        },
        {
          path: "/learning-group/:id",
          name: "learningGroup",
          props: true,
          meta: {
            title: "Группа обучения",
            onlyForEmployees: true
          },
          component: LearningGroup,
          beforeEnter: learningGroupGuard
        },
        {
          path: "/learning-group/:id/edit",
          name: "learningGroupEdit",
          props: true,
          meta: {
            title: "Группа обучения",
            onlyForEmployees: true
          },
          component: LearningGroupEdit,
          beforeEnter: learningGroupGuard
        },
        {
          path: "/import/:id",
          name: "ImportView",
          props: true,
          meta: {
            title: i18n.tc(LocalesFields.Import),
            onlyForEmployees: true
          },
          component: ImportView,
          beforeEnter: middlewareLogged
        },
        {
          path: "/import-list",
          name: "ImportList",
          props: true,
          meta: {
            title: i18n.tc(LocalesFields.Import),
            onlyForEmployees: true
          },
          component: ImportList
        },
        {
          path: "/export-list",
          name: "exportList",
          props: true,
          meta: {
            title: i18n.tc(LocalesFields.Export),
            onlyForEmployees: true
          },
          component: ExportList
        },
        {
          path: "/export/:id",
          name: "exportView",
          props: true,
          meta: {
            title: i18n.tc(LocalesFields.Export),
            onlyForEmployees: true
          },
          component: ExportView,
          beforeEnter: middlewareLogged
        },
        {
          path: "/error",
          name: "error",
          component: Error,
          meta: {
            title: i18n.tc(LocalesFields.Error)
          },
          beforeEnter: middlewareLogged
        },
        {
          path: "/instructions",
          name: "instructions",
          component: Instructions,
          meta: {
            title: i18n.tc(LocalesFields.Instructions)
          },
          beforeEnter: middlewareLogged
        },
        {
          path: "/instructions/:id",
          name: "instructionsView",
          component: InstructionsView,
          meta: {
            title: i18n.tc(LocalesFields.Instructions)
          },
          beforeEnter: middlewareLogged
        }
      ]
    },
    {
      path: "",
      component: BaseAuth,
      children: [
        {
          path: "/login",
          name: "login",
          meta: {
            title: i18n.tc(LocalesFields.Login)
          },
          component: LoginView,
          beforeEnter: middlewareNotLogged
        },
        {
          path: "/logout",
          name: "logout",
          meta: {
            title: i18n.tc(LocalesFields.Logout)
          },
          beforeEnter(to, from, next) {
            logout();
            next({ name: "login" });
          }
        },
        {
          path: "/agreement-not-logged",
          name: "agreementNot",
          meta: {
            title: "Пользовательское соглашение"
          },
          component: Agreement,
          beforeEnter: middlewareNotLogged
        },
        {
          path: "/password-recovery",
          name: "passwordRecovery",
          meta: {
            title: "Восстановление пароля"
          },
          component: PasswordRecovery,
          beforeEnter: middlewareNotLogged,
          alias: ["confirm"]
        },
        {
          path: "*",
          redirect: "/error"
        }
      ]
    }
  ],
  scrollBehavior(to, from, savedPosition) {
    return { x: 0, y: 0 };
  }
});

router.beforeEach((to, from, next) => {
  store.commit("validate/CLEAR_VALIDATION_FORMS");
  store.commit("finalDocuments/CLEAR");

  const companyTitle = store.getters?.selectedCompany?.title;

  if (!to.meta?.title.includes(companyTitle)) {
    to.meta.title = to.meta.title + (companyTitle ? " | " + companyTitle : "");
  }

  if (to.meta?.access) {
    if (checkRoles(to.meta?.access)) {
      next();
    } else {
      next({ name: "error" });
    }
  } else {
    next();
  }
});

router.afterEach((to, from) => {
  if (matomoModule()) {
    //@ts-ignore
    window._paq.push(["setCustomUrl", "/" + to.fullPath]);
    //@ts-ignore
    window._paq.push(["setDocumentTitle", to.meta?.title]);
    //@ts-ignore
    window._paq.push(["trackPageView"]);
  }
});

export default router;
